import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { shipment } from '../../../../../models/entities/shipment/shipment';
import { fedexShipBookResponse, fedexShipPreviewResponse } from '../../../../../models/entities/shipment/fedexShipPoc';
import { localSDK } from '../../../../../sdk';
import Loader from '../../../../shared/SmallComponents/Loader';
import { styled } from '../../../../shared/Theme/theme';

type Props = {
    shipment: shipment;
    onClose: () => void;
    onBooked: () => void;
};

const ModalHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    gap: 12px;
`;

const Row = styled(Box)`
    margin-bottom: 14px;
`;

const Label = styled(Typography)`
    color: #667085;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
`;

const Actions = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px 20px;
    border-top: 1px solid #e0e0e0;

    & button {
        min-height: 30px;
    }
`;

function downloadPdf(fileName: string, pdfBase64: string) {
    const binary = window.atob(pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}

function readError(err: unknown): string {
    const message = err && typeof err === 'object' && 'message' in err ? String((err as { message?: string }).message) : 'Booking failed';
    try {
        const parsed = JSON.parse(message) as { message?: string };
        if (parsed?.message) return parsed.message;
    } catch {
        return message;
    }
    return message;
}

const FedexBookSection: FC<Props> = ({ shipment, onClose, onBooked }) => {
    const [preview, setPreview] = useState<fedexShipPreviewResponse | null>(null);
    const [result, setResult] = useState<fedexShipBookResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isBooking, setIsBooking] = useState(false);

    const loadPreview = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await localSDK.shipments().fedexShipPreview({ shipmentId: shipment.id });
            setPreview(data);
        } catch (err) {
            setError(readError(err));
        } finally {
            setIsLoading(false);
        }
    }, [shipment.id]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const handleBook = async () => {
        setIsBooking(true);
        setError(null);
        try {
            const booked = await localSDK.shipments().fedexShipBook({ shipmentId: shipment.id });
            setResult(booked);
            onBooked();
        } catch (err) {
            setError(readError(err));
        } finally {
            setIsBooking(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <ModalHeader>
                <Box>
                    <Typography variant="h6">Book with FedEx</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {preview?.czNumber || shipment.CargoZoneNumber}
                    </Typography>
                </Box>
                <IconButton aria-label="Close" onClick={onClose} sx={{ width: 44, height: 44 }}>
                    <Close />
                </IconButton>
            </ModalHeader>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                {isLoading && <Loader />}
                {!isLoading && error && (
                    <Alert severity="error" sx={{ mb: 2, alignItems: 'flex-start', '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}>
                        {error}
                    </Alert>
                )}
                {!isLoading && preview && !result && (
                    <>
                        <Row>
                            <Label>From</Label>
                            <Typography>{preview.fromLabel}</Typography>
                        </Row>
                        <Row>
                            <Label>To</Label>
                            <Typography>{preview.toLabel}</Typography>
                        </Row>
                        <Row>
                            <Label>Service</Label>
                            <Typography>{preview.serviceLabel}</Typography>
                        </Row>
                        <Row>
                            <Label>Packages</Label>
                            <Typography>{preview.packageLabel}</Typography>
                        </Row>
                        {preview.alreadyBooked && (
                            <Alert severity="success">Tracking number already on this shipment: {preview.alreadyBooked}</Alert>
                        )}
                    </>
                )}
                {result && (
                    <Alert severity="success">
                        <div>Tracking number: {result.trackingNumber}</div>
                        <div>{result.message}</div>
                        {result.savedToShipment && <div>Saved on the shipment as the tracking number.</div>}
                        {result.labelSaved && <div>Label saved on the shipment documents.</div>}
                        {result.labels && result.labels.length > 0 ? (
                            result.labels.map((label) => (
                                <Button
                                    key={label.fileName}
                                    onClick={() => downloadPdf(label.fileName, label.pdfBase64)}
                                    sx={{ display: 'block', mt: 1, minHeight: 44, px: 0 }}
                                >
                                    Download {label.fileName}
                                </Button>
                            ))
                        ) : (
                            <div>FedEx did not return a label PDF.</div>
                        )}
                        <Box component="a" href={result.trackingUrl} target="_blank" rel="noreferrer" sx={{ display: 'inline-block', mt: 1, minHeight: 44 }}>
                            Open FedEx tracking
                        </Box>
                    </Alert>
                )}
            </DialogContent>
            <Actions>
                <Button onClick={onClose} sx={{ minWidth: 88 }}>
                    Close
                </Button>
                {!result && (
                    <Button
                        variant="contained"
                        onClick={handleBook}
                        disabled={isLoading || isBooking || !preview?.canBook}
                        sx={{ minWidth: 140 }}
                    >
                        {isBooking ? 'Booking…' : 'Book shipment'}
                    </Button>
                )}
            </Actions>
        </Dialog>
    );
};

export default FedexBookSection;
